import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query'
import { UseQueryOptions } from 'react-query/types/react/types'
import axios, { AxiosError } from 'axios'
import sortBy from 'lodash/sortBy'

import * as urls from './urls'

import {
  CalendarCourtReporterAdminEvent,
  CourtReporter,
  CourtReporterAdminEventDetail,
  CourtReporterDeliverable,
  CourtReporterDeliverableType,
  CourtReporterDeliverableUploadUrl,
  CourtReporterEventDetail,
} from '@/apiClient'
import { getApiErrorMessage } from '@/utils/apiError'
import { uploadPresignedS3Post } from '@/utils/s3'

type CourtReporterBookingEvent = Pick<
  CalendarCourtReporterAdminEvent,
  'court_reporter_booking_url'
>

type CourtReporterBookingOptions = {
  onSuccess: () => void
  onErrorMessage: (error: string) => void
}

export const useCourtReportersQuery = () =>
  useQuery('court_reporters', () =>
    axios
      .get<CourtReporter[]>(urls.adminCourtReporters())
      .then(r => r.data)
      .then(courtReporters =>
        sortBy(courtReporters, r => r.name.toLowerCase()),
      ),
  )

export const useCourtReporterQuery = (id: string) =>
  useQuery<CourtReporter, AxiosError>(['court_reporter', id], () =>
    axios.get<CourtReporter>(urls.courtReporter(id)).then(r => r.data),
  )

export const getCourtReporterEventQueryOptions = (
  client: QueryClient,
  id: string,
  courtReporterId: string,
  options?: Omit<
    UseQueryOptions<CourtReporterEventDetail, AxiosError>,
    'queryKey' | 'queryFn'
  >,
): UseQueryOptions<CourtReporterEventDetail, AxiosError> => {
  const queryKey = ['court_reporter_event', courtReporterId, id]
  const error = client.getQueryState<CourtReporterEventDetail, AxiosError>(
    queryKey,
  )?.error

  return {
    ...options,
    queryKey,
    queryFn: () =>
      axios
        .get<CourtReporterEventDetail>(
          urls.courtReporterEvent(courtReporterId, id),
        )
        .then(r => r.data),
    // Stop automatic refresh once the event is no longer theirs.
    ...(error?.response?.status === 404 && {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
  }
}

export const useCourtReporterEventQuery = (
  id: string,
  courtReporterId: string,
  options?: Omit<
    UseQueryOptions<CourtReporterEventDetail, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const client = useQueryClient()
  return useQuery(
    getCourtReporterEventQueryOptions(client, id, courtReporterId, options),
  )
}

export const useAdminCourtReporterEventQuery = (
  id: string,
  options?: Omit<
    UseQueryOptions<CourtReporterAdminEventDetail, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CourtReporterAdminEventDetail, AxiosError>(
    ['admin_court_reporter_event', id],
    () =>
      axios
        .get<CourtReporterAdminEventDetail>(urls.adminCourtReporterEvent(id))
        .then(r => r.data),
    options,
  )

type DeliverablesEvent = { deliverables_url: string }

const deliverablesQueryKey = (event: DeliverablesEvent) => [
  'court_reporter_deliverables',
  event.deliverables_url,
]

export const useDeliverablesQuery = (event: DeliverablesEvent) =>
  useQuery<CourtReporterDeliverable[], AxiosError>(
    deliverablesQueryKey(event),
    () =>
      axios
        .get<CourtReporterDeliverable[]>(event.deliverables_url)
        .then(r => r.data),
  )

export const useUploadDeliverableMutation = (event: DeliverablesEvent) => {
  const queryClient = useQueryClient()

  return useMutation<
    CourtReporterDeliverable,
    AxiosError<{ detail?: string }>,
    { deliverable: CourtReporterDeliverableType; file: File; mimeType: string }
  >(
    async ({ deliverable, file, mimeType }) => {
      const { data: upload } =
        await axios.post<CourtReporterDeliverableUploadUrl>(
          `${event.deliverables_url}/upload-url`,
          { deliverable, filename: file.name, mime_type: mimeType },
        )
      await uploadPresignedS3Post({ file, presignedPost: upload.presign_data })

      const { data } = await axios.post<CourtReporterDeliverable>(
        event.deliverables_url,
        { deliverable, key: upload.key, filename: file.name },
      )
      return data
    },
    {
      onSettled: () =>
        queryClient.invalidateQueries(deliverablesQueryKey(event)),
    },
  )
}

export const useRemoveDeliverableMutation = (event: DeliverablesEvent) => {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    AxiosError<{ detail?: string }>,
    CourtReporterDeliverable
  >(
    deliverable =>
      axios
        .delete(`${event.deliverables_url}/${deliverable.id}`)
        .then(() => undefined),
    {
      onSettled: () =>
        queryClient.invalidateQueries(deliverablesQueryKey(event)),
    },
  )
}

export const useBookCourtReporterMutation = (
  event: CourtReporterBookingEvent,
  options: CourtReporterBookingOptions,
) =>
  useMutation((payload: { court_reporter_id: CourtReporter['id'] }) =>
    axios
      .post(event.court_reporter_booking_url, payload)
      .then(options.onSuccess, error =>
        options.onErrorMessage(
          getApiErrorMessage(error, 'Could not book the court reporter'),
        ),
      ),
  )

export const useUnbookCourtReporterMutation = (
  event: CourtReporterBookingEvent,
  options: CourtReporterBookingOptions,
) =>
  useMutation(() =>
    axios
      .delete(event.court_reporter_booking_url)
      .then(options.onSuccess, error =>
        options.onErrorMessage(
          getApiErrorMessage(error, 'Could not cancel the booking'),
        ),
      ),
  )
